import { Button, Flex, Text } from "@mantine/core";
import { useState } from "react";

import { useAnalytics } from "@/analytics";
import { useLogger, usePartner, useSessionId } from "@/sdk";

import type { IntegratedSalesSteps } from "../types";

import { postDeclination } from "../../api";
import { useProductOfferStore } from "../../stores";
import { useIntegratedFlowStore } from "../../stores/useIntegratedFlowStore";
import { SaleProgress } from "../../types";
import { RightArrowIcon } from "../Icons";
import classes from "./SendLink.module.css";

interface SendLinkFooterProps {
    handleSendLinkClick: () => void;
    isCommunicationInfoCollected?: boolean;
    isLoading?: boolean;
    integratedStep: IntegratedSalesSteps;
    isChat?: boolean;
}

export const SendLinkFooter = ({
    handleSendLinkClick,
    isCommunicationInfoCollected,
    isLoading,
    integratedStep,
    isChat = false,
}: SendLinkFooterProps) => {
    const logger = useLogger({ module: "SendLinkFooter", supportTeam: "sales" });
    const [declining, setDeclining] = useState(false);
    const partner = usePartner();
    const { dispatcher } = useAnalytics();
    const sessionId = useSessionId();
    const product = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    const updateProduct = useProductOfferStore((state) => state.updateProduct);
    const updateIntegratedStep = useIntegratedFlowStore((state) => state.updateIntegratedStep);
    const { maximumSmartLinksGenerated } = useProductOfferStore
        .getState()
        .getSmartLinkMetaBySessionId(sessionId, product?.id ?? "");
    const decline = async () => {
        try {
            if (product) {
                await postDeclination(logger, {
                    sessionId,
                    partner,
                    productOfferId: product.id,
                    productSku: product.productSku,
                });
                updateProduct(sessionId, product.id, { saleProgress: SaleProgress.Declined });
                setDeclining(false);
            }
            void dispatcher.withExtra({ nextStep: integratedStep }).dispatchBusinessEvent("ButtonClicked_Declined");
        } catch (error) {
            setDeclining(false);
            logger.warn({ error }, "Error declining enrollment");
        }
    };
    const handleBackClick = () => {
        void dispatcher
            .withExtra({
                nextStep: integratedStep,
            })
            .dispatchBusinessEvent("ButtonClicked_Back");
        updateIntegratedStep(
            sessionId,
            product?.isProductAsurionBilled ? "CollectCustomerInfoStep" : "SuggestedOfferStep",
        );
    };
    return (
        <Flex className={classes.footerBox}>
            <Flex justify="left" align="center">
                <Button
                    onClick={handleBackClick}
                    size="xs"
                    variant="transparent"
                    color="dark.1"
                    fz="xs"
                    leftSection={<RightArrowIcon height="12" width="12" x="-1" />}
                >
                    Back
                </Button>
            </Flex>

            <Flex align="center" justify="right" gap="xs">
                {/* TODO: unified responsive design so we don't need an isChat check
                 * 3 button width causes an overflow issue with smaller sales panels
                 */}
                {!isChat && (
                    <Button
                        size="xs"
                        variant="transparent"
                        color="dark.1"
                        fz="xs"
                        loading={declining}
                        onClick={() => {
                            setDeclining(true);
                            void decline();
                        }}
                    >
                        <Text td="underline" size="xs">
                            Decline
                        </Text>
                    </Button>
                )}
                <Button
                    size="xs"
                    onClick={handleSendLinkClick}
                    loading={isLoading}
                    disabled={!isCommunicationInfoCollected || maximumSmartLinksGenerated}
                    color="primary.6"
                    fz="xs"
                    rightSection={isChat ? <RightArrowIcon height={16} width={16} /> : null}
                >
                    {isChat ? "Send to customer" : "Send link"}
                </Button>
            </Flex>
        </Flex>
    );
};
